import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  List,
  ListItem,
  Box,
  CircularProgress,
  Divider,
} from '@mui/material';
import { colors } from '../../colors';
import axiosInstance from '../../axiosConfig';

const ArchivedSurveyModal = ({ open, onClose, surveyId }) => {
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      const fetchSurveyDetails = async () => {
        try {
          const surveyResponse = await axiosInstance.get(`/api/archived/surveys/${surveyId}`);
          const responsesResponse = await axiosInstance.get(`/api/archived/responses/${surveyId}`);
          setSurvey(surveyResponse.data);
          setResponses(responsesResponse.data);
        } catch (error) {
          console.error('Error fetching archived survey details:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchSurveyDetails();
    }
  }, [open, surveyId]);

  const handleDownload = async () => {
    try {
      const response = await axiosInstance.get(`/api/archived/surveys/${surveyId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${survey.surveyName}_responses.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading responses:', error);
      alert('Failed to download responses.');
    }
  };

  const renderSectionResponses = (sectionResponses) => {
    return Object.entries(sectionResponses).map(([sectionIndex, questions]) => (
        <Box
            key={sectionIndex}
            sx={{
                mb: 3,
                p: 2,
                border: '1px solid #F0F0F0',
                borderRadius: '8px',
                backgroundColor: '#FAFAFA',
            }}
        >
            <Typography
                variant="body1"
                sx={{ fontWeight: 'bold', color: colors.primaryApplicationText, mb: 1 }}
            >
                Section {sectionIndex}:
            </Typography>
            {Object.entries(questions).length > 0 ? (
                <List>
                    {Object.entries(questions).map(([questionId, answer]) => (
                        <ListItem key={questionId} sx={{ paddingLeft: '0' }}>
                            <Typography variant="body2" sx={{ color: '#555' }}>
                                <strong>Question {questionId}:</strong> {answer}
                            </Typography>
                        </ListItem>
                    ))}
                </List>
            ) : (
                <Typography variant="body2" color="textSecondary">
                    No responses for this section.
                </Typography>
            )}
        </Box>
    ));
};

if (loading) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogContent>
                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                    <CircularProgress />
                </Box>
            </DialogContent>
        </Dialog>
    );
}

return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        {/* Dialog Title */}
        <DialogTitle
            sx={{
                backgroundColor: '#F5F5F5',
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '1.5rem',
                color: colors.primaryApplicationText,
            }}
        >
            {survey.surveyName}
        </DialogTitle>

        {/* Dialog Content */}
        <DialogContent sx={{ padding: '20px 30px' }}>
            {/* Survey Details */}
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="h6"
                    sx={{ mb: 1, color: colors.primaryBannerBlue, fontWeight: 'bold' }}
                >
                    Survey Details
                </Typography>
                <Typography
                    variant="body1"
                    sx={{ lineHeight: '1.6', color: '#666', mb: 2 }}
                >
                    {survey.surveyDescription}
                </Typography>
                <Divider sx={{ mb: 3 }} />
            </Box>

            {/* Responses Section */}
            <Box>
                <Typography
                    variant="h6"
                    sx={{ mb: 2, color: colors.primaryBannerBlue, fontWeight: 'bold' }}
                >
                    Responses
                </Typography>
                <List>
                    {responses.map((response) => (
                        <Box
                            key={response.id}
                            sx={{
                                mb: 3,
                                p: 2,
                                border: '1px solid #E0E0E0',
                                borderRadius: '8px',
                                backgroundColor: '#FFFFFF',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 'bold',
                                    color: colors.primaryApplicationText,
                                    mb: 1,
                                }}
                            >
                                Participant ID: {response.participantId}
                            </Typography>
                            {renderSectionResponses(response.sectionResponses)}
                        </Box>
                    ))}
                </List>
            </Box>
        </DialogContent>

        {/* Dialog Actions */}
        <DialogActions
            sx={{
                justifyContent: 'space-between',
                backgroundColor: '#F5F5F5',
                padding: '16px 24px',
            }}
        >
            <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                    textTransform: 'none',
                    borderColor: colors.primaryBannerBlue,
                    color: colors.primaryBannerBlue,
                    '&:hover': {
                        backgroundColor: '#E3F2FD',
                    },
                }}
            >
                Close
            </Button>
            <Button
                variant="contained"
                color="primary"
                onClick={handleDownload}
                sx={{
                    textTransform: 'none',
                    padding: '8px 20px',
                    fontWeight: 'bold',
                    backgroundColor: colors.primaryBannerBlue,
                    '&:hover': {
                        backgroundColor: colors.primaryApplicationText,
                    },
                }}
            >
                Download Responses
            </Button>
        </DialogActions>
    </Dialog>
);

};

export default ArchivedSurveyModal;
